import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Play, CheckCircle2, Clock, Sparkles, Lock } from 'lucide-react';

const REASON_COPY = {
  COMPLETE_PLAN_CLASS_FIRST: "Finish today's planned class first — then this short extra unlocks.",
  DAILY_CAP_REACHED: "That's your movement for today — rest well, more tomorrow.",
  UPGRADE_REQUIRED: 'Extra classes are part of the full SoulAI plan.',
  COMPLETED: 'Extra class done — lovely work today.',
};

// V31 — "One more short class?" (daily intensity cap). Only renders when /today offers an extra.
export const TodayExtraCard = ({ refreshKey, watchPct = 90, onPlay, onRate, onUnlock }) => {
  const [today, setToday] = useState(null);

  const fetchToday = useCallback(() => {
    api.get('/api/prenatal-coach/today').then((r) => setToday(r.data)).catch(() => setToday(null));
  }, []);

  useEffect(() => { fetchToday(); }, [fetchToday, refreshKey]);

  const extra = today?.recommendations?.find((r) => r.source === 'extra');
  if (!extra) return null;
  const done = extra.reason === 'COMPLETED' || extra.status === 'completed';
  const canPlay = extra.available && !done;
  const moderate = extra.intensity === 'moderate';

  return (
    <Card className={`rounded-2xl ${done ? 'bg-emerald-50/50 border-emerald-100' : canPlay ? 'border-primary/30 bg-white' : 'bg-muted/30 border-dashed'}`}
      data-testid="today-extra-card">
      <CardContent className="pt-4 pb-4 flex items-center gap-3 flex-wrap">
        <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${canPlay ? 'bg-primary/10' : 'bg-muted'}`}>
          {done ? <CheckCircle2 className="h-4 w-4 text-emerald-700" /> : canPlay ? <Sparkles className="h-4 w-4 text-primary" /> : <Lock className="h-3.5 w-3.5 text-muted-foreground" />}
        </div>
        <div className="flex-1 min-w-[200px]">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">One more short class today?</p>
          <p className="text-sm font-medium truncate" data-testid="today-extra-title">{extra.title}</p>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <Badge className={`text-[10px] ${moderate ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-emerald-50 text-emerald-800 border-emerald-200'}`}>{extra.intensity}</Badge>
            {extra.duration_min && <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" />{extra.duration_min} min</span>}
            <span className="text-xs text-muted-foreground" data-testid="today-remaining">{today.videos_remaining_today} of {today.daily_video_cap} left today</span>
          </div>
          {!canPlay && <p className="text-[11px] text-muted-foreground mt-1" data-testid="today-extra-reason">{REASON_COPY[extra.reason] || ''}</p>}
          {canPlay && (extra.watch_pct || 0) > 0 && (
            <div className="flex items-center gap-2 mt-1.5"><Progress value={extra.watch_pct} className="h-1 w-28" /><span className="text-[10px] text-muted-foreground">{extra.watch_pct}% watched</span></div>
          )}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {done ? null : extra.reason === 'UPGRADE_REQUIRED' ? (
            <Button size="sm" variant="outline" className="h-8 text-muted-foreground" onClick={onUnlock} data-testid="today-extra-unlock"><Lock className="h-3.5 w-3.5 mr-1" />Unlock</Button>
          ) : canPlay ? (
            <>
              <Button size="sm" variant="outline" className="h-8" onClick={() => onPlay?.({ video_id: extra.video_id, title: extra.title, duration_minutes: extra.duration_min, watch_pct: extra.watch_pct })} data-testid="today-extra-play">
                <Play className="h-3.5 w-3.5 mr-1" />Play
              </Button>
              <Button size="sm" className="h-8" disabled={(extra.watch_pct || 0) < watchPct}
                onClick={() => onRate?.({ video_id: extra.video_id, title: extra.title })} data-testid="today-extra-done">
                <CheckCircle2 className="h-3.5 w-3.5 mr-1" />Done
              </Button>
            </>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
};
